import React, { useEffect, useMemo, useState } from "react";
import styles from "./projectsSection.module.css";

/*Tetra CDMX*/
import p1_cover1 from "./images/tetra_p_1/cover.png";
import p1_g1 from "./images/tetra_p_1/1.png";
import p1_g2 from "./images/tetra_p_1/2.png";
import p1_before from "./images/tetra_p_1/before.png";
import p1_after from "./images/tetra_p_1/after.png"; 

/*Sistema contra incendios */
import p2_cover1 from "./images/incendios_p_2/cover.png";
import p2_g1 from "./images/incendios_p_2/1.png";
import p2_g2 from "./images/incendios_p_2/2.png";
import p2_before from "./images/incendios_p_2/before.png";
import p2_after from "./images/incendios_p_2/after.png";

/*Remodelación de inmueble*/
import p3_cover1 from "./images/zaragoza_p_3/cover.png";
import p3_g1 from "./images/zaragoza_p_3/1.png";
import p3_g2 from "./images/zaragoza_p_3/2.png";
import p3_g3 from "./images/zaragoza_p_3/3.png";
import p3_before from "./images/zaragoza_p_3/before.png";
import p3_after from "./images/zaragoza_p_3/after.png";

type Project = {
  id: string;
  title: string;
  description: string;
  location?: string;
  cover: string;
  gallery: string[];
  beforeAfter?: { before: string; after: string };
  tags?: string[];
  facts?: { label: string; value: string }[];
};

const PROJECTS: Project[] = [
  {
    id: "p1",
    title: "SISTEMA TETRA — SITE S.T.C., Delicias CDMX.",
    description: "Diseño e implementación de conectividad con redundancia y canalización técnica.",
    location: "CDMX",
    cover: p1_cover1,
    gallery: [
      p1_g1,
      p1_g2,
    ],
    beforeAfter: {
      before: p1_before,
      after: p1_after,
    },
    tags: ["Telecom", "Infraestructura"],
    facts: [
      { label: "Alcance", value: "Backbone + distribución" },
      { label: "Entrega", value: "4 semanas" },
      { label: "Resultado", value: "Mayor estabilidad y cobertura" },
    ],
  },
  {
    id: "p2",
    title: "INSTALACIÓN DE SITIOS RF - TETRA VERACRUZ",
    description: "Proyecto integral de adecuación eléctrica y telecomunicaciones que incluye la instalación de gabinete BTS, el montaje de antenas en torre y la ejecución de las adecuaciones necesarias para el cableado estructurado de antenas.",
    location: "Veracruz",
    cover: p2_cover1,
    gallery: [
      p2_g1,
      p2_g2,
    ],
    beforeAfter: {  
      before: p2_before,
      after: p2_after,
    },
    tags: ["Telecomunicaciones", "Radio Frecuencia"],
    facts: [
      { label: "Tipo", value: "Adecuación / obra" },
      { label: "Entrega", value: "6 semanas" },
      { label: "Enfoque", value: "Operación + mantenimiento" },
    ],
  },
  {
    id: "p3",
    title: "REMODELACIÓN DE INMUEBLE - Departamento, Zaragoza.",
    description: "Proyecto integral de diseño y renovación de espacios arquitectónicos.",
    location: "CDMX.",
    cover: p3_cover1,
    gallery: [
      p3_g1,
      p3_g2,
      p3_g3,
    ],
      beforeAfter: {
      before: p3_before,
      after: p3_after,
    },
    tags: ["Arquitectura", "Sostenibilidad"],
    facts: [
      { label: "Tipo", value: "Adecuación / obra" },
      { label: "Entrega", value: "6 semanas" },
      { label: "Enfoque", value: "Operación + mantenimiento" },
    ],
  },
];

function useScrollLock(locked: boolean) {
  useEffect(() => {
    if (!locked) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [locked]);
}

export default function ProjectsSection() {
  const [active, setActive] = useState<Project | null>(null);
  const [tab, setTab] = useState<"overview" | "beforeAfter">("overview");
  const [baMode, setBaMode] = useState<"before" | "after">("after");
  const [selectedImg, setSelectedImg] = useState<string | null>(null);

  useScrollLock(!!active);

  const open = (p: Project) => {
    setActive(p);
    setTab(p.beforeAfter ? "beforeAfter" : "overview");
    setBaMode("after");
    setSelectedImg(p.cover);
  };

  const close = () => {
    setActive(null);
    setSelectedImg(null);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    if (active) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]);

  const thumbnails = useMemo(() => {
    if (!active) return [];
    const set = new Set<string>();
    const push = (s?: string) => s && !set.has(s) && (set.add(s), true);
    const out: string[] = [];

    if (push(active.cover)) out.push(active.cover);
    active.gallery.forEach((g) => push(g) && out.push(g));
    if (active.beforeAfter) {
      push(active.beforeAfter.before) && out.push(active.beforeAfter.before);
      push(active.beforeAfter.after) && out.push(active.beforeAfter.after);
    }
    return out.slice(0, 10);
  }, [active]);

  const heroImg = useMemo(() => {
    if (!active) return null;
    if (tab === "beforeAfter" && active.beforeAfter) {
      return baMode === "before" ? active.beforeAfter.before : active.beforeAfter.after;
    }
    return selectedImg || active.cover;
  }, [active, tab, baMode, selectedImg]);

  return (
    <section className={styles.projectsSection} id="proyectos">
      <header className={styles.header}>
        <h2>Proyectos Destacados</h2>
        <p>
          Evidencia visual y resultados. Una selección de proyectos en telecomunicaciones y arquitectura
          con enfoque profesional y ejecución controlada.
        </p>
      </header>

      {/* Desktop grid */}
      <div className={styles.desktopGrid} role="list">
        {PROJECTS.map((p) => (
          <article
            key={p.id}
            className={styles.card}
            role="listitem"
            tabIndex={0}
            onClick={() => open(p)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " " ? open(p) : null)}
            aria-label={`Abrir proyecto: ${p.title}`}
          >
            <div className={styles.imageWrapper}>
              <img src={p.cover} alt={p.title} loading="lazy" />
            </div>

            <div className={styles.content}>
              <h3>{p.title}</h3>
              <span>{p.description}</span>

              <div className={styles.metaRow}>
                {p.location && <span className={styles.metaPill}>{p.location}</span>}
                {p.tags?.slice(0, 2).map((t) => (
                  <span key={t} className={styles.metaPillSoft}>
                    {t}
                  </span>
                ))}
              </div>

              <div className={styles.moreRow}>
                <span className={styles.moreHint}>Ver detalle</span>
                <span className={styles.moreArrow} aria-hidden="true">
                  →
                </span>
              </div>
            </div>
          </article>
        ))}
      </div>

      {/* Mobile carousel */}
      <div className={styles.mobileCarouselWrapper}>
        <div className={styles.mobileCarousel} aria-label="Carrusel de proyectos">
          {PROJECTS.map((p) => (
            <article
              key={p.id}
              className={styles.cardMobile}
              onClick={() => open(p)}
              aria-label={`Abrir proyecto: ${p.title}`}
            >
              <div className={styles.imageWrapperMobile}>
                <img src={p.cover} alt={p.title} loading="lazy" />
              </div>
              <div className={styles.contentMobile}>
                <h3>{p.title}</h3>
                <span>{p.description}</span>
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* Drawer / Modal */}
      {active && (
        <div className={styles.drawerOverlay} role="dialog" aria-modal="true">
          <button className={styles.drawerBackdrop} onClick={close} aria-label="Cerrar" />

          <div className={styles.drawer}>
            <div className={styles.drawerTop}>
              <div className={styles.drawerTitleBlock}>
                <div className={styles.drawerKicker}>Proyecto</div>
                <div className={styles.drawerTitle}>{active.title}</div>
                <div className={styles.drawerSub}>
                  {active.location ? <span className={styles.metaPill}>{active.location}</span> : null}
                  {active.tags?.map((t) => (
                    <span key={t} className={styles.metaPillSoft}>
                      {t}
                    </span>
                  ))}
                </div>
              </div>

              <button className={styles.drawerClose} onClick={close} aria-label="Cerrar">
                ✕
              </button>
            </div>

            {/* Tabs */}
            <div className={styles.drawerTabs}>
              <button
                type="button"
                className={`${styles.tabBtn} ${tab === "overview" ? styles.isActive : ""}`}
                onClick={() => setTab("overview")}
              >
                Vista general
              </button>

              {active.beforeAfter && (
                <button
                  type="button"
                  className={`${styles.tabBtn} ${tab === "beforeAfter" ? styles.isActive : ""}`}
                  onClick={() => setTab("beforeAfter")}
                >
                  Antes / Después
                </button>
              )}
            </div>

            {/* Hero image */}
            <div className={styles.drawerHero}>
              {heroImg && <img src={heroImg} alt="Vista del proyecto" />}
              {tab === "beforeAfter" && active.beforeAfter && (
                <div className={styles.baToggle} role="group" aria-label="Antes y después">
                  <button
                    type="button"
                    className={`${styles.baBtn} ${baMode === "before" ? styles.isActive : ""}`}
                    onClick={() => setBaMode("before")}
                  >
                    Antes
                  </button>
                  <button
                    type="button"
                    className={`${styles.baBtn} ${baMode === "after" ? styles.isActive : ""}`}
                    onClick={() => setBaMode("after")}
                  >
                    Después
                  </button>
                </div>
              )}
            </div>

            <div className={styles.drawerBody}>
              {/* Left: description + facts */}
              <div className={styles.drawerCol}>
                <p className={styles.drawerDesc}>{active.description}</p>

                {active.facts?.length ? (
                  <dl className={styles.facts}>
                    {active.facts.map((f) => (
                      <div className={styles.fact} key={f.label}>
                        <dt>{f.label}</dt>
                        <dd>{f.value}</dd>
                      </div>
                    ))}
                  </dl>
                ) : null}
              </div>

              {/* Right: thumbnails */}
              <div className={styles.drawerCol}>
                <div className={styles.thumbTitle}>Galería</div>
                <div className={styles.thumbs}>
                  {thumbnails.map((src) => (
                    <button
                      key={src}
                      type="button"
                      className={`${styles.thumbBtn} ${src === heroImg ? styles.isActive : ""}`}
                      onClick={() => {
                        setTab("overview");
                        setSelectedImg(src);
                      }}
                      aria-label="Ver imagen"
                    >
                      <img src={src} alt="Miniatura" loading="lazy" />
                    </button>
                  ))}
                </div>
                <div className={styles.thumbHint}>
                  Selecciona una imagen para verla en grande.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}